import React, { useState } from 'react';
import { XIcon, FloppyDiskIcon, WarningCircleIcon, UploadIcon, User as UserIcon } from '@phosphor-icons/react';
import { supabase } from '../lib/supabase';
import { uploadToR2, deleteFromR2 } from '../lib/storage';
import { useAuth } from '../context/AuthContext';

interface EditProfileModalProps {
    profile: any;
    onClose: () => void;
    onUpdate: () => void;
}

export const EditProfileModal: React.FC<EditProfileModalProps> = ({ profile, onClose, onUpdate }) => {
    const { updateProfile } = useAuth();
    const [bio, setBio] = useState(profile.bio || '');
    const [website, setWebsite] = useState(profile.website || '');
    const [name, setName] = useState(profile.full_name || profile.name || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [avatarPreview, setAvatarPreview] = useState<string | null>(profile.avatar_url || null);

    const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setAvatarFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSave = async () => {
        setLoading(true);
        setError('');

        try {
            let avatarUrl = profile.avatar_url;

            // Upload avatar if a new file was selected
            if (avatarFile) {
                // Delete old profile picture if it exists (one picture per user)
                if (profile.avatar_url) {
                    try {
                        await deleteFromR2(profile.avatar_url);
                        console.log('🗑️ Deleted old profile picture');
                    } catch (deleteError) {
                        console.warn('Failed to delete old avatar:', deleteError);
                        // Continue even if delete fails
                    }
                }

                // Upload to R2 storage in user-specific profile folder
                avatarUrl = await uploadToR2(avatarFile, `profile/${profile.id}`, profile.id);
            }

            const { error: updateError } = await supabase
                .from('profiles')
                .update({ bio, website, full_name: name, avatar_url: avatarUrl })
                .eq('id', profile.id);

            if (updateError) throw updateError;

            // Update AuthContext to propagate changes to all sidebars immediately
            await updateProfile({ name, avatar: avatarUrl });

            onUpdate();
            onClose();
        } catch (err: any) {
            console.error('Error updating profile:', err);
            setError(err.message || 'Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-zinc-900/90 backdrop-blur-md animate-in fade-in duration-200">
            <div className="w-full max-w-2xl bg-gradient-to-br from-onyx to-carbon border border-white/10 rounded-3xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="relative bg-gradient-to-br from-indigo-900/30 via-purple-900/20 to-pink-900/30 px-8 py-6 border-b border-white/10">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-zinc-400 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-full"
                    >
                        <XIcon className="w-5 h-5" />
                    </button>
                    <h2 className="text-2xl font-bold text-white">Edit Profile</h2>
                    <p className="text-zinc-400 text-sm mt-1">Update your profile information and avatar</p>
                </div>

                {/* Content */}
                <div className="px-8 py-6 max-h-[70vh] overflow-y-auto">
                    {error && (
                        <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center gap-2">
                            <WarningCircleIcon className="w-5 h-5 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="space-y-6">
                        {/* Avatar Upload */}
                        <div>
                            <label className="block text-zinc-300 text-sm font-semibold mb-3">
                                Profile Picture
                            </label>
                            <div className="flex items-center gap-6">
                                <div className="relative group">
                                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-1">
                                        <div className="w-full h-full rounded-full bg-zinc-900 overflow-hidden">
                                            {avatarPreview ? (
                                                <img src={avatarPreview} className="w-full h-full object-cover" alt="Avatar preview" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-zinc-500">
                                                    <UserIcon className="w-10 h-10" />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="absolute inset-0 rounded-full bg-zinc-900/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                                        <UploadIcon className="w-6 h-6 text-white" />
                                    </div>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleAvatarChange}
                                        className="absolute inset-0 opacity-0 cursor-pointer"
                                    />
                                </div>
                                <div className="flex-1">
                                    <p className="text-zinc-300 text-sm mb-1">Upload a new avatar</p>
                                    <p className="text-zinc-500 text-xs">JPG, PNG or GIF. Max size 5MB.</p>
                                </div>
                            </div>
                        </div>

                        {/* Name */}
                        <div>
                            <label className="block text-zinc-300 text-sm font-semibold mb-2">
                                Display Name
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full bg-zinc-900/40 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all placeholder:text-zinc-600"
                                placeholder="Your display name"
                            />
                        </div>

                        {/* Bio */}
                        <div>
                            <label className="block text-zinc-300 text-sm font-semibold mb-2">
                                Bio
                            </label>
                            <textarea
                                value={bio}
                                onChange={(e) => setBio(e.target.value)}
                                maxLength={160}
                                className="w-full h-32 bg-zinc-900/40 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all resize-none placeholder:text-zinc-600"
                                placeholder="Tell us about yourself..."
                            />
                            <p className="text-zinc-500 text-xs mt-2 text-right">{bio.length}/160</p>
                        </div>

                        {/* Website */}
                        <div>
                            <label className="block text-zinc-300 text-sm font-semibold mb-2">
                                Website
                            </label>
                            <input
                                type="url"
                                value={website}
                                onChange={(e) => setWebsite(e.target.value)}
                                className="w-full bg-zinc-900/40 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all placeholder:text-zinc-600"
                                placeholder="https://yourwebsite.com"
                            />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-8 py-6 border-t border-white/10 bg-zinc-900/20">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="px-6 py-2.5 text-zinc-400 hover:text-white font-semibold text-sm transition-colors hover:bg-white/5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={loading}
                        className="px-8 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-bold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all hover:scale-105 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg shadow-indigo-500/20"
                    >
                        {loading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                <span>Saving...</span>
                            </>
                        ) : (
                            <>
                                <FloppyDiskIcon className="w-4 h-4" />
                                <span>Save Changes</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
